import { useEffect } from 'react'
import type { GetServerSidePropsContext } from 'next'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { BogoBanner } from '@mc/bogo'
import { Separator } from '@mc/design-system'
import { APIError } from '@mc/next-api-core'
import { getBucket } from '@mc/next-growthbook'
import { PAGE_SOURCES, PromoExplainerModal } from '@mc/promos'
import { StaticDataProvider } from '@mc/static-data-react'
import { TRACK_PROPS } from '@mc/track-event'
import { TrackingParamsProvider } from '@mc/tracking-context'
import { CategoryNavCarousel } from 'components/CategoryNavCarousel'
import { RegularFooter } from 'components/Footer/RegularFooter'
import GuestPassBanner from 'components/GuestPassBanner'
import { CoreNavigation } from 'components/Navigation/consumer'
import { trackPen7565BackendEV } from 'experiments/pen-7565_v1_promo_banner_200_classes/server'
import { trackPen7584BackendEV } from 'experiments/pen-7584_v1_promo_banner_all_classes/server'
import { trackPen7680BackendEV } from 'experiments/pen-7680_v1_promo_banner_gift_cta/server'
import { articleCategoryPath } from 'libs/paths'
import {
  getArticle,
  getArticleCategories,
  getDefaultProduct,
  getFreeTrialByToken,
  getGraphiteArticleLinks,
  GRAPHITE_RELATED_LINKS_FLAG,
  getOtherArticlesForCategory,
  getRelatedArticlesForCategory,
  getV2Categories,
} from 'libs/server/api'
import type { GraphiteArticleLinks } from 'libs/server/api'
import { looksLikeValidSlug } from 'libs/server/api/slug-validation'
import { getPageData } from 'libs/server/page-data'
import { pageErrorHandler } from 'libs/server/page-error-handler'
import { redirect } from 'libs/utils'
import 'pages/highlight-shareable.scss'
import StickyFooter from '../components/StickyFooter'
import Details from './components/Details'
import InstructorCarousel from './components/InstructorsCarousel'
import Intro from './components/Intro'
import Meta from './components/Meta'
import QuizBanner from './components/Quiz'
import RelatedArticles from './components/RelatedArticles'
import {
  trackArticleViewed,
  trackCategoryCarouselClick,
} from './components/events'
import { getRedirectPathForArticle } from './libs/articleRedirects'

const BelowTheFold = dynamic(() => import('./components/BelowTheFold'), {
  ssr: false,
})

const SharedRecipeEmailCapture = dynamic(
  () => import('./components/SharedRecipeEmailCapture'),
  { ssr: false },
)

type Ret<T extends (...args: never[]) => unknown> = Awaited<ReturnType<T>>

type ArticlePageProps = {
  annualPass: Ret<typeof getDefaultProduct>
  article: Ret<typeof getArticle>
  articleCategories: Ret<typeof getArticleCategories>
  relatedArticles?:
    | Ret<typeof getRelatedArticlesForCategory>
    | GraphiteArticleLinks['relatedArticles']
  otherArticles?:
    | Ret<typeof getOtherArticlesForCategory>
    | GraphiteArticleLinks['otherArticles']
  trial: Ret<typeof getFreeTrialByToken>
  hasCompletedQuiz: boolean
  shouldAutoOpenQuiz: boolean
}

const Article = ({
  annualPass,
  article,
  articleCategories,
  relatedArticles,
  otherArticles,
  trial,
  hasCompletedQuiz,
  shouldAutoOpenQuiz,
}: ArticlePageProps) => {
  const { query } = useRouter()

  useEffect(() => {
    trackArticleViewed(article)
  }, [article])

  return (
    <StaticDataProvider
      value={{
        annualPass,
        article,
        articleCategories,
        relatedArticles,
        otherArticles,
        trial,
      }}
      providerKey={article.slug}
    >
      <TrackingParamsProvider
        value={{ [TRACK_PROPS.PAGE_TYPE]: 'next-articles' }}
      >
        <Meta />
        <BogoBanner />
        <GuestPassBanner />
        {!hasCompletedQuiz && (
          <QuizBanner shouldAutoOpenQuiz={shouldAutoOpenQuiz} />
        )}
        <PromoExplainerModal pageSource={PAGE_SOURCES.ARTICLE_PAGE} />

        <Separator className='mc-opacity--muted mc-m-0' />
        <div className='container'>
          <div className='mc-my-2'>
            <CategoryNavCarousel
              categories={articleCategories}
              pathCategory={articleCategoryPath}
              onNavClick={trackCategoryCarouselClick}
            />
          </div>
        </div>
        <div className='mc-theme-light mc-bg-light'>
          <Intro />
          <InstructorCarousel className='col-lg-8 offset-lg-1' />
          <Details />
          <BelowTheFold />
          <RelatedArticles />
          {query.shared && <SharedRecipeEmailCapture />}
        </div>
        <StickyFooter />
      </TrackingParamsProvider>
    </StaticDataProvider>
  )
}

Article.pageSettings = {
  analyticsLabel: 'Article Page',
  showPromo: true,
  navigation: CoreNavigation,
  footer: RegularFooter,
}

const getArticleLinks = (
  ctx: GetServerSidePropsContext,
  articlePromise: ReturnType<typeof getArticle>,
) => {
  const graphiteLinks = Promise.all([
    getBucket(ctx, GRAPHITE_RELATED_LINKS_FLAG)
      .then((bucket) => bucket === 'variant_1')
      .catch(() => false),
    articlePromise,
  ]).then(([isGraphiteOn, { slug }]) =>
    isGraphiteOn ? getGraphiteArticleLinks(slug) : null,
  )

  return {
    relatedArticles: graphiteLinks.then((links) =>
      links
        ? links.relatedArticles
        : articlePromise.then((article) =>
            getRelatedArticlesForCategory(
              ctx,
              article.category.id,
              article.slug,
            ),
          ),
    ),
    otherArticles: graphiteLinks.then((links) =>
      links
        ? links.otherArticles
        : articlePromise.then(({ category }) =>
            getOtherArticlesForCategory(ctx, category.id),
          ),
    ),
  }
}

export async function getServerSideProps(ctx: GetServerSidePropsContext) {
  const {
    query: { slug },
    req: {
      cookies: {
        referral_token: referralToken,
        'quiz-completed': quizCompleted,
      },
      headers: { host, referer },
    },
  } = ctx

  if (typeof slug !== 'string' || !looksLikeValidSlug(slug)) {
    return pageErrorHandler(
      new APIError('/articles/[slug]', 400, 'Invalid slug'),
    )
  }
  const redirectLocation = getRedirectPathForArticle(slug)
  if (redirectLocation) {
    return redirect(ctx, redirectLocation, { queryExclude: ['slug'] })
  }

  await trackPen7565BackendEV(ctx)
  await trackPen7584BackendEV(ctx)
  await trackPen7680BackendEV(ctx)

  const hasCompletedQuiz = quizCompleted === 'true'
  const hasMCReferrer = referer && referer.indexOf(host || '') !== -1
  const shouldAutoOpenQuiz = !!hasMCReferrer && !hasCompletedQuiz

  const articlePromise = getArticle(ctx, slug)

  return getPageData({
    criticalPromises: {
      article: articlePromise,
    },
    nonCriticalPromises: {
      annualPass: getDefaultProduct(ctx),
      articleCategories: getArticleCategories(ctx),
      ...getArticleLinks(ctx, articlePromise),
      categories: getV2Categories(ctx),
      trial: getFreeTrialByToken(ctx, { referralToken }),
    },
    propsMapper: ({ ...restProps }) => ({
      hasCompletedQuiz: hasCompletedQuiz || false,
      shouldAutoOpenQuiz,
      ...restProps,
    }),
  })
}

export default Article
